/**
 * CSV parsing and validation utilities.
 */

export interface CsvParseResult {
  headers: string[];
  rows: string[][];
  errors: string[];
}

const MAX_CSV_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

/**
 * Parse raw CSV text into headers and rows.
 * Splits on commas (simple CSV — no quoted field support).
 */
export function parseCsv(text: string): CsvParseResult {
  const errors: string[] = [];
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 1) {
    errors.push("CSV is empty.");
    return { headers: [], rows: [], errors };
  }

  const headers = lines[0].split(",").map((h) => h.trim());
  const rows: string[][] = [];

  for (let i = 1; i < lines.length; i += 1) {
    const cols = lines[i].split(",").map((c) => c.trim());
    if (cols.length !== headers.length) {
      errors.push(`Row ${i + 1}: expected ${headers.length} columns but got ${cols.length}.`);
    }
    rows.push(cols);
  }

  return { headers, rows, errors };
}

/**
 * Validate a File object for CSV upload:
 * - Must have .csv extension
 * - Must be <= 5 MB
 *
 * Returns an error string or null if valid.
 */
export function validateCsvFile(file: File): string | null {
  if (!file.name.toLowerCase().endsWith(".csv")) {
    return "File must have a .csv extension.";
  }
  if (file.size > MAX_CSV_SIZE_BYTES) {
    return `File is too large (max ${MAX_CSV_SIZE_BYTES / 1024 / 1024} MB).`;
  }
  return null;
}

// ── N6: EAN ↔ Variation Name positional mapping ─────────────

export interface EanVariationPair {
  index: number;
  ean: string;
  variation: string;
}

export interface EanVariationPreview {
  rows: EanVariationPair[];
  warning: string | null;
}

/**
 * Build a positional preview of the EAN list ↔ Variation Names mapping.
 * Each EAN line is paired with the variation name at the same 0-based
 * index. Returns a `warning` string when the two counts don't match so
 * the UI can surface it without throwing.
 */
export function buildEanVariationPreview(
  eanText: string,
  variationText: string,
): EanVariationPreview {
  const eans = String(eanText || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const variations = String(variationText || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

  const max = Math.max(eans.length, variations.length);
  const rows: EanVariationPair[] = [];
  for (let i = 0; i < max; i += 1) {
    rows.push({
      index: i + 1,
      ean: eans[i] || "",
      variation: variations[i] || "",
    });
  }

  let warning: string | null = null;
  if (eans.length > 0 && variations.length > 0 && eans.length !== variations.length) {
    warning = `EAN count (${eans.length}) does not match variation name count (${variations.length}).`;
  }
  return { rows, warning };
}

// ── T10: Master project CSV template + row validation ──────

/** Required columns for the Master project bulk-import template (T10). */
export const MASTER_CSV_TEMPLATE_COLUMNS = [
  "project_name",
  "brand",
  "division",
  "hub",
  "type",
  "ocd_date",
  "brief_date",
  "ean_list",
  "variation_names",
] as const;

/**
 * N7 — Required columns for the Local project bulk-import template.
 * Local projects are created by Market users for a specific hub and do
 * not carry a division or a project_name (the project name is derived
 * from brand + hub by convention).
 */
export const LOCAL_CSV_TEMPLATE_COLUMNS = [
  "hub",
  "brand",
  "type",
  "ocd_date",
  "brief_date",
  "ean_list",
] as const;

/** Generate a downloadable CSV template (header row + one example row). */
export function buildMasterCsvTemplate(): string {
  const example = [
    "Lipstick X Spring",
    "Lancôme",
    "LLD",
    "FR",
    "Launch",
    "2026-04-01",
    "2026-02-15",
    "111;222;333",
    "Red;Pink;Coral",
  ];
  return `${MASTER_CSV_TEMPLATE_COLUMNS.join(",")}\n${example.join(",")}\n`;
}

/** N7 — Local project CSV template. */
export function buildLocalCsvTemplate(): string {
  const example = [
    "FR",
    "Lancôme",
    "Local",
    "2026-04-01",
    "2026-02-15",
    "111;222",
  ];
  return `${LOCAL_CSV_TEMPLATE_COLUMNS.join(",")}\n${example.join(",")}\n`;
}

export interface MasterCsvRowValidation {
  rowIndex: number;
  values: Record<string, string>;
  errors: string[];
}

/**
 * N7 — Validate a parsed CSV against the Local template. Mirrors
 * validateMasterCsvRows but uses LOCAL_CSV_TEMPLATE_COLUMNS and a
 * trimmed required-field set.
 */
export function validateLocalCsvRows(
  headers: string[],
  rows: string[][],
): MasterCsvRowValidation[] {
  const headerIndex = new Map<string, number>();
  headers.forEach((h, i) =>
    headerIndex.set(
      h.trim().toLowerCase().replace(/[^a-z0-9]/g, "_"),
      i,
    ),
  );

  return rows.map((cols, idx) => {
    const errors: string[] = [];
    const values: Record<string, string> = {};
    for (const col of LOCAL_CSV_TEMPLATE_COLUMNS) {
      const i = headerIndex.get(col);
      const v = i != null ? String(cols[i] || "").trim() : "";
      values[col] = v;
      // hub, brand, type are required
      if (!v && ["hub", "brand", "type"].includes(col)) {
        errors.push(`Missing ${col}`);
      }
      if ((col === "ocd_date" || col === "brief_date") && v && !/^\d{4}-\d{2}-\d{2}$/.test(v)) {
        errors.push(`${col} must be YYYY-MM-DD`);
      }
    }
    return { rowIndex: idx + 1, values, errors };
  });
}

/**
 * Validate a parsed CSV against the Master template. Returns one entry per
 * row (1-indexed) with errors per row. A row is considered "valid" when its
 * `errors` array is empty.
 */
export function validateMasterCsvRows(
  headers: string[],
  rows: string[][],
): MasterCsvRowValidation[] {
  const headerIndex = new Map<string, number>();
  headers.forEach((h, i) =>
    headerIndex.set(
      h.trim().toLowerCase().replace(/[^a-z0-9]/g, "_"),
      i,
    ),
  );

  return rows.map((cols, idx) => {
    const errors: string[] = [];
    const values: Record<string, string> = {};
    for (const col of MASTER_CSV_TEMPLATE_COLUMNS) {
      const i = headerIndex.get(col);
      const v = i != null ? String(cols[i] || "").trim() : "";
      values[col] = v;
      // project_name, brand, division, hub, type are required
      if (!v && ["project_name", "brand", "division", "hub", "type"].includes(col)) {
        errors.push(`Missing ${col}`);
      }
      // dates must look like YYYY-MM-DD
      if ((col === "ocd_date" || col === "brief_date") && v && !/^\d{4}-\d{2}-\d{2}$/.test(v)) {
        errors.push(`${col} must be YYYY-MM-DD`);
      }
    }
    // ean / variation lists must align if both present
    const eans = (values.ean_list || "").split(";").filter(Boolean);
    const variations = (values.variation_names || "").split(";").filter(Boolean);
    if (eans.length && variations.length && eans.length !== variations.length) {
      errors.push(
        `ean_list and variation_names must have the same number of items (${eans.length} vs ${variations.length})`,
      );
    }
    return { rowIndex: idx + 1, values, errors };
  });
}

/** Build a downloadable error-report CSV listing only invalid rows. */
export function buildErrorReportCsv(validations: MasterCsvRowValidation[]): string {
  const invalid = validations.filter((v) => v.errors.length);
  if (!invalid.length) return "row,errors\n";
  const lines = ["row,errors"];
  for (const v of invalid) {
    lines.push(`${v.rowIndex},"${v.errors.join("; ").replace(/"/g, '""')}"`);
  }
  return lines.join("\n") + "\n";
}

/**
 * Check that the given CSV headers include all expected columns.
 * Returns an array of missing column names (empty if all present).
 */
export function validateCsvHeaders(headers: string[], expected?: string[]): string[] {
  const EXPECTED_COLUMNS = expected || [
    "division",
    "brand",
    "type",
    "productname",
    "globalean",
  ];

  const normalizedHeaders = new Set(
    headers.map((h) =>
      h
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, ""),
    ),
  );

  return EXPECTED_COLUMNS.filter((col) => !normalizedHeaders.has(col));
}
